<!-- The encoded ladder, always visible, outside the player. The table skeleton stays put through
     loading and empty states so the layout does not jump -- see the UI conventions. -->
<div class="row cl-spacing-small">
  <div class="col-12">
    <h3>Encoded renditions</h3>
    <p class="valid-feedback">
      Every rendition both manifests declare. A codec is often in only one of them — HEVC ships on
      HLS, AV1 on DASH — so neither list alone shows the whole encode. Click a row to play it.
    </p>

    <table class="cl-tight">
      <thead>
        <tr>
          <th>Stream</th>
          <th>Codec</th>
          <th>Resolution</th>
          <th>Bitrate</th>
          <th>Status</th>
          <th></th>
        </tr>
      </thead>
      <tbody>
        @if (loading) {
          <tr class="cl-tr-no-data">
            <td colspan="100" style="text-align: center">
              <div class="cl-spinner">Loading ...<i class="fas fa-spinner fa-spin"></i></div>
            </td>
          </tr>
        } @else if (!entries.length) {
          <tr class="cl-tr-no-data">
            <td colspan="100" style="text-align: center">No data available.</td>
          </tr>
        } @else {
          @for (entry of entries; track $index) {
            <tr [class.cl-highlight]="isPlaying(entry)" [class.cl-faded]="!entry.playable">
              <td>{{ entry.streamType === 'HLS stream' ? 'HLS' : 'DASH' }}</td>
              <td>{{ codecName(entry.codec) }}</td>
              <td>{{ resolution(entry) }}</td>
              <td class="cl-font-monospace">{{ bitrate(entry) }}</td>
              <td>
                @if (isPlaying(entry)) {
                  <span class="cl-badge cl-badge-sm cl-color-leaf">Playing</span>
                } @else if (entry.playable) {
                  <span class="cl-badge cl-badge-sm">Playable</span>
                } @else {
                  <span class="cl-badge cl-badge-sm cl-color-honey">Unsupported</span>
                  <span class="valid-feedback"> {{ entry.reason }}</span>
                }
              </td>
              <td class="cl-td-links" width="1%">
                @if (entry.playable && !isPlaying(entry)) {
                  <a class="cl-a-btn cl-a-primary" (click)="select(entry)">Play</a>
                }
              </td>
            </tr>
            <!-- The playing row expands into a card carrying everything that only applies to what is
                 on screen. Attached to the row rather than sitting under the table, so the audio and
                 subtitle choices read as part of the rendition they belong to -- and they disappear
                 with it when playback moves elsewhere.

                 Haze, because it holds form fields: the grey ground is what makes them stand out. -->
            @if (isPlaying(entry) && (audioRenditions.length || textRenditions.length)) {
              <tr class="cl-highlight">
                <td colspan="100">
                  <div class="card cl-color-haze">
                    <div class="card-body">
                      <!-- Grouped the way the app's other form cards are, then label -> control ->
                           help text per field.

                           `cl-form` is load-bearing, not decoration: the castLabs form styles are
                           scoped to it, and this panel sits in a plain `cl-section`, not in a PO
                           form. Without it a `form-select` falls back to bare Bootstrap -- 6px
                           radius, white ground, grey border, wrong font -- instead of the 20px
                           Eggshell-on-Night control the rest of the app shows. Measured, not
                           assumed; there is a layout test on the radius. -->
                      <!-- No legend: the two labelled selects say what they are. The fieldset stays
                           because `cl-form` rides on it. -->
                      <fieldset class="cl-form cl-spacing-small">
                        <div class="row cl-spacing-small">
                          @if (audioRenditions.length) {
                            <div class="col-12 col-md-6">
                              <label class="form-label" for="ladder_audio">Audio</label>
                              <select (change)="onAudioChange($any($event.target).value)" class="form-select" id="ladder_audio">
                                @for (rendition of audioRenditions; track rendition.id) {
                                  <option [value]="rendition.id" [selected]="rendition.id === activeAudioRendition?.id">
                                    {{ audioLabel(rendition) }}
                                  </option>
                                }
                              </select>
                            </div>
                          }
                          @if (textRenditions.length) {
                            <div class="col-12 col-md-6">
                              <label class="form-label" for="ladder_text">Subtitles</label>
                              <select (change)="onTextChange($any($event.target).value)" class="form-select" id="ladder_text">
                                <option value="" [selected]="!activeTextRendition">Off</option>
                                @for (rendition of textRenditions; track rendition.id) {
                                  <option [value]="rendition.id" [selected]="rendition.id === activeTextRendition?.id">
                                    {{ textLabel(rendition) }}
                                  </option>
                                }
                              </select>
                              <p class="valid-feedback">Off is a choice, not the absence of one — pick it to clear subtitles.</p>
                            </div>
                          }
                        </div>
                      </fieldset>
                    </div>
                  </div>
                </td>
              </tr>
            }
          }
        }
      </tbody>
    </table>

    @if (failed) {
      <p class="invalid-feedback d-block">
        The manifests could not be read, so this list is unavailable. Playback itself is unaffected.
      </p>
    }
  </div>
</div>
